import { GaugeContainer } from "@mui/x-charts/Gauge";
import { useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import SpaceRiskGaugeChartSubArc from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/components/SpaceRiskGaugeChart/SpaceRiskGaugeChartSubArc";
import SpaceRiskGaugeChartMainArc from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/components/SpaceRiskGaugeChart/SpaceRiskGaugeChartMainArc";
import SpaceRiskGaugeChartPointer from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/components/SpaceRiskGaugeChart/SpaceRiskGaugeChartPointer";
import { getColorByValue } from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/components/SpaceRiskGaugeChart/utils/spaceRiskGaugeChartUtils";

interface Props {
  riskValue: number | undefined;
  gaugeWidth: number;
  isSpaceDashboard: boolean;
}

const SpaceRiskGaugeChart = ({
  riskValue,
  gaugeWidth,
  isSpaceDashboard,
}: Props) => {
  const theme = useTheme();
  const gaugeHeight = gaugeWidth * 0.88;
  // Ensure we have a valid number, defaulting to 0 if undefined/null/NaN
  const validRiskValue =
    typeof riskValue === "number" && !isNaN(riskValue) ? riskValue : 0;
  const [animatedValue, setAnimatedValue] = useState(0);
  const animationRef = useRef<number | null>(null);
  const prevValueRef = useRef<number>(0);

  // Animate the needle from 0 to riskValue
  useEffect(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    const duration = 800;
    const start = prevValueRef.current;
    const end = validRiskValue;
    const startTime = performance.now();

    function animate(now: number) {
      // Calculate how much time has passed since the animation started
      const elapsed = now - startTime;
      // Calculate the progress as a value between 0 and 1
      const progress = Math.min(elapsed / duration, 1);
      // Interpolate the value between start and end based on progress
      const value = start + (end - start) * progress;
      // Update the animated value state
      setAnimatedValue(value);
      // If the animation is not finished, request the next animation frame
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // If the animation is finished, update the previous value reference
        prevValueRef.current = end;
      }
    }
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [validRiskValue]);

  const valueColor = theme.palette[getColorByValue(animatedValue)].main;

  return (
    <GaugeContainer
      width={gaugeWidth}
      height={gaugeHeight}
      startAngle={-140}
      endAngle={140}
      value={animatedValue}
      sx={{
        margin: "auto",
      }}
    >
      <SpaceRiskGaugeChartSubArc />
      <SpaceRiskGaugeChartMainArc value={animatedValue} />
      <SpaceRiskGaugeChartPointer />

      <text
        x="53%"
        y="89%"
        id="risk-space-value"
        data-testid="risk-space-value"
        textAnchor="middle"
        fontSize="32"
        fontWeight="bold"
        style={{ fill: valueColor }}
      >
        {(typeof animatedValue === "number" && !isNaN(animatedValue)
          ? animatedValue
          : 0
        ).toFixed(2)}
      </text>

      <text
        x="52%"
        y="95%"
        textAnchor="middle"
        fontSize="12"
        style={{ fill: theme.palette.text.secondary }}
      >
        {isSpaceDashboard ? "SPACE RISK" : "WORKSPACE RISK"}
      </text>

      <text
        x={gaugeWidth * 0.26}
        y={gaugeHeight - 10}
        fontSize="12"
        style={{ fill: theme.palette.text.secondary }}
      >
        0
      </text>
      <text
        x={gaugeWidth * 0.77}
        y={gaugeHeight - 10}
        fontSize="12"
        textAnchor="end"
        style={{ fill: theme.palette.text.secondary }}
      >
        100
      </text>
    </GaugeContainer>
  );
};

export default SpaceRiskGaugeChart;
